import get from "lodash/get";
import React, { useEffect, useMemo } from "react";
import { Button, Input, Select } from "@pd/pd-ui-kit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Modal from "~/shared/ui/Fields/Modal";
import { IPartner, useGetPartnerById, useSetPartner } from "~/entity/Partners";
import {
  mapPartnerFormToCreateParams,
  mapPartnerFormToUpdateParams,
} from "~/entity/Partners/lib/mapPartnerFormToParams";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useGetProfileQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import {
  buildFullAddress,
  findGeoPlaceByName,
  GeoPlaceOption,
  resolveGeoParentId,
  useAddressGeoPlaces,
} from "~/shared/lib/addressGeo";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";

import { PARTNERS_STATUS_OPTIONS } from "../../../../MOCK/partners";

import s from "./PartnersFormModal.less";

const PARTNER_GEO_TOKENS = [
  "country",
  "fo",
  "subject",
  "district",
  "city",
  "locality",
  "street",
  "house",
] as const;

type PartnerGeoFormField =
  | "country"
  | "fo"
  | "region"
  | "district"
  | "city"
  | "locality"
  | "street"
  | "house";

const FORM_FIELD_TO_GEO_TOKEN: Record<
  PartnerGeoFormField,
  (typeof PARTNER_GEO_TOKENS)[number]
> = {
  country: "country",
  fo: "fo",
  region: "subject",
  district: "district",
  city: "city",
  locality: "locality",
  street: "street",
  house: "house",
};

const GEO_FIELD_CHILDREN: Partial<
  Record<PartnerGeoFormField, PartnerGeoFormField[]>
> = {
  country: ["fo", "region", "district", "city", "locality", "street", "house"],
  fo: ["region", "district", "city", "locality", "street", "house"],
  region: ["district", "city", "locality", "street", "house"],
  district: ["city", "locality", "street", "house"],
  city: ["locality", "street", "house"],
  locality: ["street", "house"],
  street: ["house"],
};

const REQUIRED_GEO_FIELDS: PartnerGeoFormField[] = ["country", "fo", "region"];

export interface IPartnersForm {
  name: string;
  inn: string;
  status: { value: string; label: string };
  country: GeoPlaceOption | null;
  fo: GeoPlaceOption | null;
  region: GeoPlaceOption | null;
  district: GeoPlaceOption | null;
  city: GeoPlaceOption | null;
  locality: GeoPlaceOption | null;
  street: GeoPlaceOption | null;
  house: GeoPlaceOption | null;
}

const defaultStatus = {
  value: PARTNERS_STATUS_OPTIONS[0],
  label: PARTNERS_STATUS_OPTIONS[0],
};

const defaultValues: IPartnersForm = {
  name: "",
  inn: "",
  status: defaultStatus,
  country: null,
  fo: null,
  region: null,
  district: null,
  city: null,
  locality: null,
  street: null,
  house: null,
};

const stringToGeoOption = (value: string): GeoPlaceOption | null =>
  value ? { id: value, name: value } : null;

const geoOptionToSelectValue = (option: GeoPlaceOption | null) =>
  option ? { value: option.id, label: option.name } : null;

const partnerToFormValues = (partner: IPartner): IPartnersForm => ({
  name: partner.name,
  inn: partner.inn,
  status: { value: partner.status, label: partner.status },
  country: stringToGeoOption(partner.country),
  fo: stringToGeoOption(partner.fo),
  region: stringToGeoOption(partner.region),
  district: null,
  city: stringToGeoOption(partner.city),
  locality: stringToGeoOption(partner.locality),
  street: stringToGeoOption(partner.street),
  house: partner.house
    ? {
        id: partner.house,
        name: partner.house,
        ...(partner.zipCode ? { index: partner.zipCode } : {}),
      }
    : null,
});

const PartnersFormModal = () => {
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const setParams = useURLParamsSetter();
  const isEditMode = openModal === "Edit";
  const isOpen = openModal === "Create" || isEditMode;
  const { createPartner, updatePartner } = useSetPartner();
  const { partner } = useGetPartnerById(currentId, isEditMode && !!currentId);
  const { data: profileData } = useGetProfileQuery({});
  const profileId = get(profileData, "UserGetter.getProfile.id");
  const { hasPermission } = usePermissions();
  const canArchivePartner = hasPermission("employeeAccounting.partners.delete");

  const statusOptions = PARTNERS_STATUS_OPTIONS.filter(
    (status) => canArchivePartner || status !== "Архивный",
  ).map((status) => ({
    value: status,
    label: status,
  }));

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IPartnersForm>({
    defaultValues,
  });

  const { country, fo, region, district, city, locality, street, house } =
    useWatch({
      control,
    }) as IPartnersForm;

  const geoSelections = useMemo(
    () => ({
      country,
      fo,
      subject: region,
      district,
      city,
      locality,
      street,
      house,
    }),
    [city, country, district, fo, house, locality, region, street],
  );

  const geo = useAddressGeoPlaces({
    tokens: PARTNER_GEO_TOKENS,
    selections: geoSelections,
  });

  const fullAddress = useMemo(() => {
    const built = buildFullAddress(geoSelections);

    if (built) {
      return built;
    }

    return isEditMode && partner?.address ? partner.address : "";
  }, [geoSelections, isEditMode, partner?.address]);

  const postalIndex = house?.index ?? "";

  const clearGeoChildren = (field: PartnerGeoFormField) => {
    GEO_FIELD_CHILDREN[field]?.forEach((childField) => {
      setValue(childField, null, {
        shouldDirty: true,
        shouldValidate: false,
      });
    });
  };

  const closeModal = () => {
    setParams({ openModal: undefined, currentId: undefined });
    reset(defaultValues);
  };

  useEffect(() => {
    if (isEditMode && partner) {
      reset(partnerToFormValues(partner));
      return;
    }

    if (openModal === "Create") {
      reset(defaultValues);
    }
  }, [isEditMode, partner?.id, partner?.updatedAt, openModal, reset, partner]);

  useEffect(() => {
    if (!isEditMode || !partner) {
      return;
    }

    let cancelled = false;

    const applyOption = (
      field: PartnerGeoFormField,
      option: GeoPlaceOption | null,
    ) => {
      if (!cancelled && option) {
        setValue(field, option);
      }
    };

    const resolvePrefilledGeo = async () => {
      const country = await findGeoPlaceByName("country", partner.country);
      applyOption("country", country);

      const fo = await findGeoPlaceByName("fo", partner.fo, country?.id);
      applyOption("fo", fo);

      const region = await findGeoPlaceByName(
        "subject",
        partner.region,
        fo?.id,
      );
      applyOption("region", region);

      const city = await findGeoPlaceByName("city", partner.city, region?.id);
      applyOption("city", city);

      const locality = await findGeoPlaceByName(
        "locality",
        partner.locality,
        city?.id,
      );
      applyOption("locality", locality);

      const street = await findGeoPlaceByName(
        "street",
        partner.street,
        locality?.id ?? city?.id,
      );
      applyOption("street", street);

      const house = await findGeoPlaceByName(
        "house",
        partner.house,
        street?.id ?? locality?.id ?? city?.id,
      );

      if (!cancelled && house) {
        setValue(
          "house",
          house.index
            ? house
            : {
                ...house,
                ...(partner.zipCode ? { index: partner.zipCode } : {}),
              },
        );
      }
    };

    resolvePrefilledGeo();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, partner, setValue]);

  const submit = handleSubmit((data) => {
    if (isEditMode && currentId) {
      updatePartner.mutate(
        { id: currentId, data: mapPartnerFormToUpdateParams(data) },
        { onSuccess: closeModal },
      );
      return;
    }

    createPartner.mutate(
      mapPartnerFormToCreateParams(data, Number(profileId) || undefined),
      {
        onSuccess: closeModal,
      },
    );
  });

  const isSaving = createPartner.isLoading || updatePartner.isLoading;

  const validateInn = (value: string) => {
    if (!/^\d+$/.test(value)) {
      return t("partners.modal.innOnlyDigits");
    }

    if (value.length !== 10 && value.length !== 12) {
      return t("partners.modal.innLength");
    }

    if (/^(\d)\1+$/.test(value)) {
      return t("partners.modal.innIdenticalDigits");
    }

    return true;
  };

  const renderGeoSelect = (field: PartnerGeoFormField) => {
    const token = FORM_FIELD_TO_GEO_TOKEN[field];

    return (
      <div key={field} className={s.fieldWrapper}>
        <span className={s.label}>{t(`partners.filter.${field}`)}</span>
        <Controller
          name={field}
          control={control}
          rules={
            REQUIRED_GEO_FIELDS.includes(field)
              ? { required: t("validation.required") }
              : undefined
          }
          render={({ onChange, value }) => (
            <Select
              labelInValue
              allowClear
              showSearch
              filterOption={filterOptionByLabel}
              disabled={
                token !== "country" && !resolveGeoParentId(token, geoSelections)
              }
              loading={geo.loading[token]}
              placeholder={t("partners.filter.selectPlaceholder")}
              value={geoOptionToSelectValue(value)}
              onChange={(option) => {
                if (!option) {
                  onChange(null);
                  clearGeoChildren(field);
                  return;
                }

                const matched = geo.options[token].find(
                  (item) => item.id === option.value,
                );

                onChange(
                  matched ?? {
                    id: String(option.value),
                    name: option.label,
                  },
                );
                clearGeoChildren(field);
              }}
              options={geo.options[token].map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          )}
        />
        {get(errors, `${field}.message`) && (
          <span className={s.error}>{get(errors, `${field}.message`)}</span>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      title={
        isEditMode ? t("partners.modal.editTitle") : t("partners.modal.title")
      }
      actionClose={closeModal}
      w={948}
    >
      <div className={s.form}>
        <p className={s.sectionTitle}>{t("partners.modal.section")}</p>
        <div className={s.fields}>
          <div className={s.row}>
            <div className={s.fieldWrapper}>
              <span className={s.label}>{t("partners.filter.name")}</span>
              <Controller
                name="name"
                control={control}
                rules={{ required: t("validation.required") }}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    placeholder={t("partners.filter.inputPlaceholder")}
                  />
                )}
              />
              {errors.name?.message && (
                <span className={s.error}>{errors.name.message}</span>
              )}
            </div>
            <div className={s.fieldWrapper}>
              <span className={s.label}>{t("partners.filter.inn")}</span>
              <Controller
                name="inn"
                control={control}
                rules={{
                  required: t("validation.required"),
                  validate: validateInn,
                }}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    value={value}
                    onChange={(e) =>
                      onChange(e.target.value.replace(/\D/g, "").slice(0, 12))
                    }
                    onBlur={onBlur}
                    maxLength={12}
                    placeholder={t("partners.filter.inputPlaceholder")}
                  />
                )}
              />
              {errors.inn?.message && (
                <span className={s.error}>{errors.inn.message}</span>
              )}
            </div>
            <div className={s.fieldWrapper}>
              <span className={s.label}>{t("partners.filter.status")}</span>
              <Controller
                name="status"
                control={control}
                rules={{ required: t("validation.required") }}
                render={({ onChange, value }) => (
                  <Select
                    labelInValue
                    value={value}
                    onChange={onChange}
                    options={statusOptions}
                  />
                )}
              />
              {get(errors, "status.message") && (
                <span className={s.error}>{get(errors, "status.message")}</span>
              )}
            </div>
          </div>
          <div className={s.row}>
            {(
              ["country", "fo", "region", "district"] as PartnerGeoFormField[]
            ).map(renderGeoSelect)}
          </div>
          <div className={s.row}>
            {(
              ["city", "locality", "street", "house"] as PartnerGeoFormField[]
            ).map(renderGeoSelect)}
          </div>
          <div className={s.row}>
            <div className={`${s.fieldWrapper} ${s.fieldQuarter}`}>
              <span className={s.label}>{t("partners.filter.index")}</span>
              <Input
                value={postalIndex}
                disabled
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            </div>
          </div>
          <div className={s.fieldFull}>
            <span className={s.label}>{t("partners.filter.fullAddress")}</span>
            <Input
              value={fullAddress}
              disabled
              placeholder={t("partners.filter.inputPlaceholder")}
            />
          </div>
        </div>
        <div className={s.buttons}>
          <Button
            bold
            color="secondary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={closeModal}
            disabled={isSaving}
          >
            {t("partners.modal.cancel")}
          </Button>
          <Button
            bold
            color="primary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={submit}
            disabled={isSaving}
          >
            {t("partners.modal.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PartnersFormModal;
